import { showError, showSuccess } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { fetchCurrentSubscription } from 'features/billing/actions/fetch_current_subscription'
import { fetchNextBill } from 'features/billing/actions/fetch_next_bill'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { trackEvent } from 'hooks/analytics_events/useTrack'
import { uuidSelector } from 'selectors/current_user'

import {
  isTaxInformationCannotValidateModalVisibleSelector, taxInformationModalPopupLocation,
  taxInformationModalValidatedFormDataSelector
} from '../../selectors'

export const defaultInitialValues = {
  countryCode: 'AF',
  postalCode: '',
  streetAddress: '',
  streetAddressTwo: '',
  city: '',
  stateProvince: '',
  companyName: '',
  taxId: '',
  canadaFederalTaxId: '',
  canadaProvincialTaxId: '',
  australiaTaxId: '',
}

const initialState = {
  initialValues: defaultInitialValues,
  invalidFormFields: [],
  isSuccessVisible: false,
  isConfirmationVisible: false,
  isDeleteVisible: false,
  isCannotValidatePopupVisible: false,
  isVisible: false,
  validatedFormData: {
    australiaTaxId: '',
    canadaFederalTaxId: '',
    canadaProvincialTaxId: '',
    city: '',
    companyName: '',
    countryCode: '',
    postalCode: '',
    stateProvince: '',
    streetAddress: '',
    streetAddressTwo: '',
    taxId: ''
  },
  updateTaxIdAndShippingAddress: { loading: false },
  validationCall: { loading: false },
  shouldUpdateExistingSubscription: true,
  confirmedTaxInformation: null,
  popupLocation: null,
  showThankYouPopup: true
}

export const updateTaxIdAndShippingAddress = createAsyncThunk(
  'taxInformationModal/updateTaxIdAndShippingAddress',
  async (_, { dispatch, getState }) => {
    const state = getState()
    const validatedFormData = taxInformationModalValidatedFormDataSelector(state)
    const isCannotValidateModalVisible = isTaxInformationCannotValidateModalVisibleSelector(state)

    const {
      streetAddress,
      streetAddressTwo,
      city,
      stateProvince,
      postalCode,
      countryCode,
      companyName,
      australiaTaxId,
      canadaFederalTaxId,
      canadaProvincialTaxId,
      taxId
    } = validatedFormData

    const formattedTaxIds = () => {
      switch (countryCode) {
        case 'AU':
          return { taxId: australiaTaxId, countryCode, postalCode }
        case 'CA': {
          const result = {}

          if (canadaFederalTaxId) result.taxId = canadaFederalTaxId
          if (canadaProvincialTaxId) result.secondaryTaxId = canadaProvincialTaxId

          return { countryCode, postalCode, ...result }
        }
        default:
          return { taxId, countryCode, postalCode }
      }
    }

    try {
      const [taxIdResponse, shippingAddressResponse] = await Promise.all([
        httpRequest({
          url: '/api/billing/subscription/tax_id',
          method: 'PUT',
          data: {
            tax_id: formattedTaxIds()
          },
        }),
        httpRequest({
          url: '/api/billing/subscription/shipping_address',
          method: 'PUT',
          data: { shipping_address: {
            city,
            company: companyName,
            country: countryCode,
            addr: streetAddress,
            extended_addr: streetAddressTwo,
            zip: postalCode,
            state_code: stateProvince
          } },
        }),
      ])

      const [taxIdSuccess] = taxIdResponse
      const [shippingAddressSuccess] = shippingAddressResponse

      if (!taxIdSuccess || !shippingAddressSuccess) {
        dispatch(showError(I18n.t('errors.billing.server')))
        return false
      }

      dispatch(fetchCurrentSubscription())
      dispatch(fetchNextBill())

      if (isCannotValidateModalVisible) {
        dispatch(showSuccess(I18n.t('publisher.billing.vat_id_popup.cannot_validate_popup.success_toast')))
      }

      return true
    } catch (error) {
      dispatch(showError(I18n.t('errors.billing.server')))
      return false
    }
  }
)

export const validateTaxIds = createAsyncThunk(
  'taxInformationModal/validateTaxIds',
  async ({ taxIds, countryCode, postalCode, taxIdMap, shouldUpdateExistingSubscription }, { dispatch, rejectWithValue, getState }) => {
    const state = getState()
    const userUuid = uuidSelector(state)
    const validatedFormData = taxInformationModalValidatedFormDataSelector(state)
    const popupLocation = taxInformationModalPopupLocation(state)

    const [success, response] = await httpRequest({
      url: '/api/billing/tax_id_validation',
      method: 'POST',
      params: {
        tax_ids: taxIds,
        address: {
          country: countryCode,
          postal_code: postalCode
        }
      }
    })

    if (!success) return rejectWithValue(response)

    const value = response.value

    if (typeof value === 'object') {
      const invalidTaxIds = value.collection.filter((t) => t.valid === false)
      const externalValidationSuccess = value.collection.every((t) => t.externally_validated === true)

      if (invalidTaxIds.length === 0 && externalValidationSuccess) {
        if (shouldUpdateExistingSubscription) {
          dispatch(updateTaxIdAndShippingAddress())
        } else {
          return { success: true, storeLocally: true, confirmedTaxInformation: validatedFormData }
        }
        return { success: true }
      }

      if (invalidTaxIds.length === 0 && !externalValidationSuccess) {
        // No invalid tax IDs but unable to externally validate via Anrok. Show "Can you proceed" modal
        return { success: false, error: 'anrok_error' }
      }

      // 1) Find invalid tax ID in validatedFormData
      // 2) Send back to form with that tax ID field having errors
      const invalidFields = []

      invalidTaxIds.forEach((itd) => {
        for (const [k, v] of Object.entries(taxIdMap)) {
          if (v === itd.tax_id) {
            invalidFields.push(k)
          }
        }
      })

      return { success: false, invalidFields, error: 'invalid_fields' }
    }

    if (typeof value === 'string' && value.includes('HTTP status code: 400')) {
      if (userUuid) {
        await trackEvent({
          name: TRACKING_EVENT_NAMES.ERROR_STATE_SHOWN,
          dependencies: { user_uuid: userUuid },
          properties: {
            error_name: 'tax_information_address_invalid',
            location: popupLocation
          }
        })
      }
      return { success: false, invalidFields: ['postalCode'], error: 'invalid_fields' }
    }

    const anrokErrorExists = (anrokString) => {
      const statusCodes = ['500', '502', '504']
      return statusCodes.some((sc) => anrokString.includes(`HTTP status code: ${sc}`))
    }

    if (typeof value === 'string' && anrokErrorExists(value)) {
      return { success: false, error: 'anrok_error' }
    }

    return value
  }
)

const taxInformationModalSlice = createSlice({
  name: 'taxInformationModal',
  initialState,
  reducers: {
    clearInvalidFormFields: (state) => {
      state.invalidFormFields = []
    },
    resetPopup: () => initialState,
    setDeleteVisibility: (state, { payload }) => {
      state.isDeleteVisible = payload
    },
    setConfirmationVisibility: (state, { payload }) => {
      state.isConfirmationVisible = payload
    },
    setInitialValues: (state, { payload }) => {
      state.initialValues = payload
    },
    setPopupLocation: (state, { payload }) => {
      state.popupLocation = payload
    },
    setValidatedFormData: (state, { payload }) => {
      state.validatedFormData = payload
    },
    setVisibility: (state, { payload }) => {
      state.isVisible = payload
    },
    setShouldUpdateExistingSubscription: (state, { payload }) => {
      state.shouldUpdateExistingSubscription = payload
    },
    setConfirmedTaxInformation: (state, { payload }) => {
      state.confirmedTaxInformation = payload
    },
    setShowThankYouPopup: (state, { payload }) => {
      state.showThankYouPopup = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(validateTaxIds.pending, (state) => {
      state.validationCall = { loading: true }
    })
    builder.addCase(validateTaxIds.fulfilled, (state, { payload }) => {
      state.validationCall = { loading: false, response: payload }

      if (payload.success && payload.storeLocally) {
        state.confirmedTaxInformation = payload.confirmedTaxInformation
        state.isConfirmationVisible = false
        state.isVisible = false
      }

      if (!payload.success && payload.error === 'invalid_fields') {
        state.initialValues = state.validatedFormData
        state.invalidFormFields = payload.invalidFields || []
        state.validationCall = { loading: false }
        state.isConfirmationVisible = false
      }

      if (!payload.success && payload.error === 'anrok_error') {
        state.isCannotValidatePopupVisible = true
      }
    })
    builder.addCase(validateTaxIds.rejected, (state, { payload }) => {
      state.validationCall = { loading: false, error: payload }
    })
    builder.addCase(updateTaxIdAndShippingAddress.pending, (state) => {
      state.updateTaxIdAndShippingAddress = { loading: true }
    })
    builder.addCase(updateTaxIdAndShippingAddress.fulfilled, (state, { payload }) => {
      state.updateTaxIdAndShippingAddress = { loading: false, response: payload }
      if (payload && !state.isCannotValidatePopupVisible) state.isSuccessVisible = true
      if (payload && state.isCannotValidatePopupVisible) {
        Object.keys(initialState).forEach((key) => {
          state[key] = initialState[key]
          // Set state = initialState via Immer, because we can't call resetPopup() or return initialState
        })
      }
    })
    builder.addCase(updateTaxIdAndShippingAddress.rejected, (state, { payload }) => {
      state.updateTaxIdAndShippingAddress = { loading: false, error: payload }
    })
  }
})

export const {
  reducer,
  actions: {
    clearInvalidFormFields,
    resetPopup,
    setVisibility,
    setConfirmationVisibility,
    setPopupLocation,
    setDeleteVisibility,
    setValidatedFormData,
    setInitialValues,
    setShouldUpdateExistingSubscription,
    setConfirmedTaxInformation,
    setShowThankYouPopup
  }
} = taxInformationModalSlice
